import React, { useEffect, useState, useRef } from "react";
import { Plus, Megaphone, Trash2, Pencil, Copy, Pause, Play, Rocket, Upload, Sparkles, Loader2, X, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";

const MODES = [{ id: "popup", l: "Pop-up" }, { id: "banner", l: "Banner" }, { id: "highlight", l: "In evidenza" }];
const FREQ = [{ id: "every_visit", l: "Ogni visita" }, { id: "session", l: "Una volta per sessione" }, { id: "days", l: "Ogni N giorni" }];
const TARGETS = [{ id: "none", l: "Nessun pulsante" }, { id: "menu", l: "Un menù" }, { id: "page", l: "Una pagina" }, { id: "url", l: "Link esterno" }];
const empty = () => ({ title: "", text: "", image: "", button_label: "", button_target_type: "none", button_target_value: "", display_mode: "popup", all_pages: true, all_menus: true, page_ids: [], menu_ids: [], delay_seconds: 2, frequency_type: "session", frequency_days: 1, priority: 1, status: "draft", days: [], start: "", end: "" });

const todayStr = () => new Date().toISOString().slice(0, 10);
function effState(p) {
  if (p.status === "draft") return { l: "Bozza", cls: "text-muted-foreground border" };
  if (p.status === "paused") return { l: "In pausa", cls: "text-muted-foreground border" };
  const t = todayStr();
  if (p.start && t < String(p.start).slice(0, 10)) return { l: "Programmata", cls: "bg-amber-50 text-amber-600" };
  if (p.end && t > String(p.end).slice(0, 10)) return { l: "Scaduta", cls: "bg-red-50 text-red-600" };
  if (p.button_target_type !== "none" && !p.button_target_value) return { l: "Link mancante", cls: "bg-red-50 text-red-600" };
  return { l: "Attiva", cls: "bg-emerald-50 text-emerald-600" };
}
const resolveImg = (img) => (!img ? "" : img.startsWith("http") ? img : api.mediaUrl(img));

export default function PromotionsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [menus, setMenus] = useState([]);
  const [pages, setPages] = useState([]);
  const [ed, setEd] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const [p, m, pg] = await Promise.all([api.promoList(), api.menuList(), api.pageList()]); setItems(p.data); setMenus(m.data); setPages(pg.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (publish) => {
    if (!ed.title.trim()) { toast({ title: "Inserisci un titolo" }); return; }
    const body = { ...ed };
    if (publish !== undefined) body.status = publish ? "published" : "draft";
    try {
      if (ed.id) await api.promoUpdate(ed.id, body); else await api.promoCreate(body);
      toast({ title: publish ? "Promozione pubblicata" : "Bozza salvata" }); setEd(null); load();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail, variant: "destructive" }); }
  };
  const toggle = async (p) => { await api.promoUpdate(p.id, { status: p.status === "published" ? "paused" : "published" }); load(); };
  const dup = async (p) => { await api.promoDuplicate(p.id); load(); };
  const del = async (p) => { await api.promoDelete(p.id); toast({ title: "Archiviata" }); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-2xl md:text-3xl font-extrabold">Promozioni e pop-up</h1><p className="text-muted-foreground mt-1">Messaggi mostrati nelle tue pagine e nei menù pubblici.</p></div>
        <Button className="btn-grad text-white font-semibold gap-2" onClick={() => setEd(empty())}><Plus className="h-4 w-4" /> Nuova</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Caricamento…</p> : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Megaphone className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="font-semibold mt-3">Nessuna promozione</p>
          <Button className="btn-grad text-white font-semibold gap-2 mt-4" onClick={() => setEd(empty())}><Plus className="h-4 w-4" /> Nuova</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => {
            const st = effState(p);
            return (
              <div key={p.id} className="rounded-2xl border border-border bg-white overflow-hidden card-shadow">
                {resolveImg(p.image) && <img src={resolveImg(p.image)} alt="" className="h-28 w-full object-cover" />}
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <Badge className={`text-[10px] ${st.cls}`}>{st.l}</Badge>
                    <Badge variant="outline" className="text-[10px]">{MODES.find((m) => m.id === p.display_mode)?.l}</Badge>
                  </div>
                  <h3 className="font-display font-bold mt-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.text}</p>
                  <p className="text-xs text-muted-foreground mt-2">{p.views || 0} viste · {p.clicks || 0} clic</p>
                  <div className="flex gap-1.5 mt-3">
                    <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => setEd({ ...p })}><Pencil className="h-3.5 w-3.5" /> Modifica</Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(p)} title={p.status === "published" ? "Pausa" : "Pubblica"}>{p.status === "published" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Button>
                    <Button size="sm" variant="ghost" onClick={() => dup(p)}><Copy className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => del(p)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!ed} onOpenChange={(o) => !o && setEd(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[92vh]">
          {ed && <PromoEditor ed={ed} setEd={setEd} menus={menus} pages={pages} onSave={save} onClose={() => setEd(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PromoEditor({ ed, setEd, menus, pages, onSave, onClose }) {
  const { toast } = useToast();
  const [adv, setAdv] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setEd({ ...ed, [k]: v });

  const uploadImg = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.uploadMedia(fd); set("image", data.id); }
    catch (err) { toast({ title: "Caricamento non riuscito", description: err?.response?.data?.detail, variant: "destructive" }); }
    finally { setUploading(false); }
  };
  const aiImprove = async () => {
    if (!ed.title && !ed.text) { toast({ title: "Scrivi prima titolo o testo" }); return; }
    setAiBusy(true);
    try { const { data } = await api.aiText({ action: "improve", text: ed.text || ed.title, context: `promozione: ${ed.title}` }); set("text", data.result); toast({ title: "Testo migliorato dall'AI" }); }
    catch { toast({ title: "AI non disponibile", variant: "destructive" }); }
    finally { setAiBusy(false); }
  };

  const st = effState(ed);
  return (
    <div className="grid md:grid-cols-[1fr_320px] max-h-[92vh]">
      {/* editor */}
      <div className="overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-extrabold">{ed.id ? "Modifica promozione" : "Nuova promozione"}</h2>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Contenuto</p>
        <div className="space-y-3 mb-5">
          <div><Label className="text-xs text-muted-foreground">Titolo</Label><Input value={ed.title} onChange={(e) => set("title", e.target.value)} className="h-10 mt-1" placeholder="Es. Aperitivo 2×1 fino alle 20" /></div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-xs text-muted-foreground">Testo</Label>
              <button onClick={aiImprove} disabled={aiBusy} className="text-xs text-primary font-semibold inline-flex items-center gap-1">{aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Migliora con AI</button>
            </div>
            <Textarea value={ed.text} onChange={(e) => set("text", e.target.value)} className="mt-1 min-h-[70px]" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Immagine</Label>
            <div className="flex gap-2 mt-1">
              <Button type="button" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica foto</Button>
              <Input value={ed.image?.startsWith("http") ? ed.image : ""} onChange={(e) => set("image", e.target.value)} className="h-10 flex-1" placeholder="oppure incolla URL" />
              {ed.image && <button onClick={() => set("image", "")} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImg} />
          </div>
        </div>

        <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Destinazione</p>
        <div className="space-y-3 mb-5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">Pulsante apre</Label><Select value={ed.button_target_type} onValueChange={(v) => set("button_target_type", v) || setEd({ ...ed, button_target_type: v, button_target_value: "" })}><SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{TARGETS.map((t) => <SelectItem key={t.id} value={t.id}>{t.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Etichetta</Label><Input value={ed.button_label} onChange={(e) => set("button_label", e.target.value)} className="h-10 mt-1" disabled={ed.button_target_type === "none"} /></div>
          </div>
          {ed.button_target_type === "menu" && <Select value={ed.button_target_value} onValueChange={(v) => set("button_target_value", v)}><SelectTrigger className="h-10"><SelectValue placeholder="Scegli menù" /></SelectTrigger><SelectContent>{menus.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select>}
          {ed.button_target_type === "page" && <Select value={ed.button_target_value} onValueChange={(v) => set("button_target_value", v)}><SelectTrigger className="h-10"><SelectValue placeholder="Scegli pagina" /></SelectTrigger><SelectContent>{pages.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent></Select>}
          {ed.button_target_type === "url" && <Input value={ed.button_target_value} onChange={(e) => set("button_target_value", e.target.value)} className="h-10" placeholder="https://" />}
        </div>

        <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">Programmazione</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">Dal</Label><Input type="date" value={ed.start || ""} onChange={(e) => set("start", e.target.value)} className="h-10 mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Al</Label><Input type="date" value={ed.end || ""} onChange={(e) => set("end", e.target.value)} className="h-10 mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2"><span className="text-sm">Su tutte le pagine</span><Switch checked={ed.all_pages} onCheckedChange={(v) => set("all_pages", v)} /></div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2"><span className="text-sm">Su tutti i menù</span><Switch checked={ed.all_menus} onCheckedChange={(v) => set("all_menus", v)} /></div>
          </div>
        </div>

        <button onClick={() => setAdv(!adv)} className="flex items-center gap-1 text-sm font-semibold text-muted-foreground mt-5"><ChevronDown className={`h-4 w-4 transition-transform ${adv ? "rotate-180" : ""}`} /> Impostazioni avanzate</button>
        {adv && (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div><Label className="text-xs text-muted-foreground">Modalità</Label><Select value={ed.display_mode} onValueChange={(v) => set("display_mode", v)}><SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{MODES.map((m) => <SelectItem key={m.id} value={m.id}>{m.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Frequenza</Label><Select value={ed.frequency_type} onValueChange={(v) => set("frequency_type", v)}><SelectTrigger className="h-10 mt-1"><SelectValue /></SelectTrigger><SelectContent>{FREQ.map((m) => <SelectItem key={m.id} value={m.id}>{m.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Ritardo (sec)</Label><Input type="number" value={ed.delay_seconds} onChange={(e) => set("delay_seconds", +e.target.value)} className="h-10 mt-1" /></div>
            <div><Label className="text-xs text-muted-foreground">Priorità</Label><Input type="number" value={ed.priority} onChange={(e) => set("priority", +e.target.value)} className="h-10 mt-1" /></div>
            {ed.frequency_type === "days" && <div><Label className="text-xs text-muted-foreground">Ogni N giorni</Label><Input type="number" value={ed.frequency_days} onChange={(e) => set("frequency_days", +e.target.value)} className="h-10 mt-1" /></div>}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">La frequenza è riconosciuta per browser/dispositivo, non identifica la singola persona. Le date usano il fuso locale.</p>
      </div>

      {/* live preview */}
      <div className="bg-muted/40 border-l border-border p-5 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-muted-foreground">Anteprima {ed.display_mode === "popup" ? "pop-up" : ed.display_mode === "banner" ? "banner" : "in evidenza"}</span>
          <Badge className={`text-[10px] ${st.cls}`}>{st.l}</Badge>
        </div>
        <div className="flex-1 grid place-items-center">
          {ed.display_mode === "banner" ? (
            <div className="w-full btn-grad text-white rounded-xl px-3 py-2.5 text-sm text-center font-medium">{ed.title || "Titolo banner"}{ed.button_label ? ` · ${ed.button_label}` : ""}</div>
          ) : (
            <div className="w-full max-w-[230px] rounded-2xl bg-white shadow-xl overflow-hidden">
              {resolveImg(ed.image) && <img src={resolveImg(ed.image)} alt="" className="w-full aspect-video object-cover" />}
              <div className="p-4 text-center">
                <h3 className="font-display font-extrabold">{ed.title || "Titolo promozione"}</h3>
                {ed.text && <p className="text-xs text-muted-foreground mt-1.5">{ed.text}</p>}
                {ed.button_label && <div className="btn-grad text-white rounded-lg px-3 py-2 mt-3 text-sm font-semibold">{ed.button_label}</div>}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={() => onSave()}>Salva bozza</Button>
          <Button className="flex-1 btn-grad text-white font-semibold gap-1.5" onClick={() => onSave(true)}><Rocket className="h-4 w-4" /> Pubblica</Button>
        </div>
      </div>
    </div>
  );
}
